import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FaceScanDialog } from "@/components/FaceScanDialog";
import { timeOf } from "@/components/ClockWidget";

const errText = (e, d) => { const x = e.response?.data?.detail; return typeof x === "string" ? x : e.message || d; };

const getGeo = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) return reject(new Error("Location is not supported on this device"));
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
    () => reject(new Error("Location permission denied. Allow location on this device to clock in.")),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 });
});

const LiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(i); }, []);
  return (
    <div data-testid="kiosk-clock">
      <div className="text-xs sm:text-sm uppercase tracking-[0.25em] text-slate-400">{fmtDate(now.toISOString())}</div>
      <div className="font-mono font-semibold text-5xl sm:text-7xl lg:text-8xl mt-2 tabular-nums">
        {now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
      </div>
    </div>
  );
};

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "back"];

const Keypad = ({ code, setCode, busy }) => {
  const press = (k) => {
    if (busy) return;
    if (k === "clear") setCode("");
    else if (k === "back") setCode(code.slice(0, -1));
    else if (code.length < 5) setCode(code + k);
  };
  useEffect(() => {
    const h = (e) => { if (/^\d$/.test(e.key)) press(e.key); else if (e.key === "Backspace") press("back"); else if (e.key === "Escape") press("clear"); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });
  return (
    <div className="w-full max-w-sm" data-testid="kiosk-keypad">
      <p className="text-sm text-slate-300 text-center mb-4">Enter the <b className="text-white">last 5 digits</b> of your Biometric ID</p>
      <div className="flex justify-center gap-3 mb-6" data-testid="kiosk-code-dots">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-12 w-10 sm:h-14 sm:w-12 rounded-xl border flex items-center justify-center text-2xl font-mono transition-colors duration-150 ${code[i] ? "border-sky-400 bg-sky-500/15 text-white" : "border-white/15 bg-white/5 text-transparent"}`}>{code[i] ? "•" : "0"}</div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {KEYS.map((k) => (
          <button key={k} type="button" onClick={() => press(k)} disabled={busy}
            className="h-14 sm:h-16 rounded-2xl bg-white/10 hover:bg-white/20 active:scale-95 transition-transform duration-100 text-2xl font-medium disabled:opacity-40 flex items-center justify-center"
            data-testid={`kiosk-key-${k}`}>
            {k === "clear" ? <span className="text-sm text-slate-300">Clear</span> : k === "back" ? <Icons.Delete className="h-6 w-6" /> : k}
          </button>
        ))}
      </div>
    </div>
  );
};

const Candidate = ({ c, onClock, busy }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl bg-white/10 p-4" data-testid={`kiosk-candidate-${c.id}`}>
    <div className="flex items-center gap-4 min-w-0 flex-1">
    {c.photo_url ? <img src={c.photo_url} alt="" className="h-16 w-16 rounded-full object-cover border-2 border-white/20" />
      : <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center"><Icons.User className="h-7 w-7 text-slate-400" /></div>}
    <div className="min-w-0 flex-1">
      <div className="text-lg font-semibold truncate" data-testid="kiosk-candidate-name">{c.name}</div>
      <div className="text-xs text-slate-400 truncate">{[c.employee_no, c.job_level, c.company_name].filter(Boolean).join(" · ")}</div>
      <div className="text-xs text-slate-300 mt-1">In {timeOf(c.clock_in)} · Out {timeOf(c.clock_out)}{!c.face_registered && <span className="text-amber-300"> · Face setup on first use</span>}</div>
    </div>
    </div>
    <Button onClick={() => onClock(c)} disabled={busy} size="lg"
      className={`h-14 px-6 text-base w-full sm:w-auto shrink-0 ${c.clocked_in ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}`} data-testid="kiosk-clock-btn">
      {c.clocked_in ? <><Icons.LogOut className="h-5 w-5 mr-2" />Clock Out</> : <><Icons.LogIn className="h-5 w-5 mr-2" />Clock In</>}
    </Button>
  </div>
);

const Result = ({ r }) => (
  <div className="flex flex-col items-center text-center gap-3 uemp-fade-in" data-testid="kiosk-result">
    {r.face_verified ? <Icons.CheckCircle2 className="h-20 w-20 text-emerald-400" /> : <Icons.AlertTriangle className="h-20 w-20 text-amber-400" />}
    <div className="text-2xl sm:text-3xl font-semibold" data-testid="kiosk-result-name">{r.name}</div>
    <div className="text-lg text-slate-200" data-testid="kiosk-result-text">{r.type === "in" ? "Clocked in" : "Clocked out"} at {timeOf(r.time)}{r.type === "out" && r.hours != null ? ` · ${r.hours}h` : ""}</div>
    {!r.face_verified && <div className="text-sm text-amber-300">Face not verified — sent to HR for review</div>}
  </div>
);

const useFullscreen = () => {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const h = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const enter = () => { setFull(true); document.documentElement.requestFullscreen?.().catch(() => {}); };
  const exit = () => { setFull(false); if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); };
  return { full, enter, exit };
};

export default function SharedClock() {
  const [allowed, setAllowed] = useState(null);
  const [code, setCode] = useState("");
  const [cands, setCands] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null);
  const [result, setResult] = useState(null);
  const fs = useFullscreen();
  const idle = useRef(null);

  useEffect(() => { api.get("/attendance/kiosk/access").then(() => setAllowed(true)).catch(() => setAllowed(false)); }, []);
  const reset = useCallback(() => { setCode(""); setCands(null); setResult(null); setScan(null); }, []);

  useEffect(() => {
    if (code.length !== 5) return;
    setBusy(true);
    api.post("/attendance/kiosk/lookup", { bio_code: code }).then(({ data }) => setCands(data))
      .catch((e) => { toast.error(errText(e, "Not found")); setCode(""); }).finally(() => setBusy(false));
  }, [code]);

  useEffect(() => {
    clearTimeout(idle.current);
    if (result) idle.current = setTimeout(reset, 5000);
    else if (cands && !scan) idle.current = setTimeout(reset, 45000);
    return () => clearTimeout(idle.current);
  }, [result, cands, scan, reset]);

  const startClock = async (c) => {
    setBusy(true);
    try { const geo = await getGeo(); setScan({ c, geo, mode: c.face_registered ? "verify" : "register" }); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const onFace = async ({ descriptor, photo, liveness }) => {
    const { c, geo } = scan;
    const type = c.clocked_in ? "out" : "in";
    setScan(null); setBusy(true);
    try {
      const { data } = await api.post("/attendance/kiosk/clock", { ...geo, descriptor, photo, liveness, bio_code: code, employee_id: c.id, type });
      setResult({ ...data, type, time: type === "in" ? data.clock_in : data.clock_out });
    } catch (e) { toast.error(errText(e, "Clock failed")); }
    setBusy(false);
  };

  if (allowed === null) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 p-6" data-testid="kiosk-loading"><Icons.Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;
  }
  if (allowed === false) {
    return <div className="bg-white rounded-xl border border-slate-200 p-6 text-sm text-slate-600 max-w-xl" data-testid="kiosk-denied">Your role cannot open the Shared Clock In/Out. Ask HR to add <b>Shared Clock In/Out</b> to your HRIS role.</div>;
  }

  return (
    <div className={`${fs.full ? "fixed inset-0 z-[45] rounded-none" : "relative rounded-2xl min-h-[calc(100vh-8rem)]"} bg-slate-950 text-white overflow-hidden flex flex-col`} data-testid="shared-clock">
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-sky-600/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-20 h-96 w-96 rounded-full bg-emerald-600/10 blur-3xl pointer-events-none" />
      <div className="relative flex items-center justify-between gap-3 px-5 sm:px-8 pt-5">
        <div className="flex items-center gap-2 text-sm text-slate-300 min-w-0"><Icons.MonitorSmartphone className="h-4 w-4 text-sky-400 shrink-0" /><span className="hidden sm:inline">Shared Clock In/Out</span></div>
        <Button variant="outline" onClick={fs.full ? fs.exit : fs.enter} className="bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white" data-testid="kiosk-fullscreen-btn">
          {fs.full ? <><Icons.Minimize2 className="h-4 w-4 mr-1.5" />Exit full screen</> : <><Icons.Maximize2 className="h-4 w-4 mr-1.5" />Full screen</>}
        </Button>
      </div>
      <div className="relative flex-1 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center px-5 sm:px-10 py-8">
        <div className="text-center lg:text-left min-w-0"><LiveClock />
          <p className="mt-6 text-slate-400 text-sm max-w-md mx-auto lg:mx-0">Enter your 5 digits, pick your name, then look at the camera. Your face and location are checked for every clock in and out.</p>
        </div>
        <div className="flex flex-col items-center gap-4 w-full min-w-0">
          {result ? <Result r={result} /> : cands ? (
            <div className="w-full max-w-xl space-y-3" data-testid="kiosk-candidates">
              <p className="text-sm text-slate-300">{cands.length > 1 ? "Several employees share these digits — tap your name" : "Is this you?"}</p>
              {cands.map((c) => <Candidate key={c.id} c={c} onClock={startClock} busy={busy} />)}
              <button onClick={reset} className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5 pt-2" data-testid="kiosk-cancel-btn"><Icons.ArrowLeft className="h-4 w-4" />Not me — start over</button>
            </div>
          ) : <Keypad code={code} setCode={setCode} busy={busy} />}
          {busy && <div className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="kiosk-busy"><Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Please wait…</div>}
        </div>
      </div>
      <FaceScanDialog open={!!scan} mode={scan?.mode || "verify"} onOpenChange={(v) => { if (!v) setScan(null); }} onResult={onFace} />
    </div>
  );
}
